/**
 * Geocoding API service
 * Handles location search and reverse geocoding
 */

import { get, buildQueryString } from './api';
import type { Bounds } from '@/types/grid';

/**
 * Geocoding search result
 */
export interface GeocodingResult {
  id: string;
  name: string;
  displayName: string;
  country: string;
  type: string;
  bounds: Bounds;
  center: {
    lat: number;
    lng: number;
  };
}

/**
 * API endpoints
 */
const ENDPOINTS = {
  SEARCH: '/geocoding/search',
  REVERSE: '/geocoding/reverse',
};

/**
 * Search for locations by query
 * @param query - Search query (city, region, country name)
 * @param limit - Max number of results (default 10)
 * @returns Array of geocoding results with bounds
 */
export async function searchLocations(
  query: string,
  limit = 10
): Promise<GeocodingResult[]> {
  if (!query || query.trim().length < 2) {
    return [];
  }

  const queryString = buildQueryString({ q: query, limit });
  return get<GeocodingResult[]>(`${ENDPOINTS.SEARCH}${queryString}`);
}

/**
 * Reverse geocode coordinates to location
 * @param lat - Latitude
 * @param lng - Longitude
 * @returns Location info with bounds
 */
export async function reverseGeocode(
  lat: number,
  lng: number
): Promise<GeocodingResult | null> {
  try {
    const queryString = buildQueryString({ lat, lng });
    return await get<GeocodingResult>(`${ENDPOINTS.REVERSE}${queryString}`);
  } catch {
    return null;
  }
}

/**
 * Get location name for bounds (using center point)
 * Useful for reverse geocoding a selected area
 */
export async function getLocationNameForBounds(bounds: Bounds): Promise<string | null> {
  const centerLat = (bounds.north + bounds.south) / 2;
  const centerLng = (bounds.east + bounds.west) / 2;

  const result = await reverseGeocode(centerLat, centerLng);
  if (result) {
    return `${result.name}, ${result.country}`;
  }
  return null;
}
